import React, { Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import Button from '../../../../../../components/global/Button';
import { getThumbBySize } from '../../../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../../../constants/image-size-codes';
import {
  getAccountProfileName,
  getAccountProfileUsername,
} from '../../helpers';
import Angle from '../../../../../../components/global/Angle';
import { isProAccount } from '../../../../../../util/account';
import { getAsset } from '../../../../../../util/assets';
import DropdownMenu from '../../../../../../components/global/DropdownMenu';
import DotTrigger from '../../../../../../components/global/DotTrigger';
import AccountBlockModal from '../AccountBlockModal';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import AccountShareModal, { ACCOUNT_SHARE_MODAL } from '../AccountShareModal';

const propTypes = {
  accountProfile: PropTypes.shape({
    id: PropTypes.string,
    logo_image: PropTypes.shape({
      url: PropTypes.string,
    }),
    hero_image: PropTypes.shape({
      url: PropTypes.string,
    }),
    organization_name: PropTypes.string,
    friend_status: PropTypes.shape({
      is_friend: PropTypes.bool,
      pending_friend_request: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }),
  onGoBack: PropTypes.func,
};

const ProfilePageHero = ({ accountProfile, onGoBack }) => {
  const openModal = useOpenModal();

  const accountAvatar = accountProfile.avatar_photo
    ? getThumbBySize(
        accountProfile.avatar_photo.thumbnail_urls,
        IMAGE_SIZE_CODE.LARGE,
      )?.url
    : undefined;

  const accountAvatarUrl = accountAvatar ?? getAsset('/user-menu-default.png');

  const displayName = getAccountProfileName(accountProfile);
  const username = getAccountProfileUsername(accountProfile);
  const emailAddress = accountProfile?.photo_attribution_page?.email_address;
  const hasHeroImage = !!accountProfile?.hero_image?.url;
  const eventCount = isProAccount(accountProfile)
    ? (accountProfile.past_hierarchy_items?.total ?? 0) +
      (accountProfile.future_hierarchy_items?.total ?? 0)
    : accountProfile.hierarchy_items?.total ?? 0;

  const classes = classNames({
    'profile-page-hero': true,
    'has-image': hasHeroImage,
  });

  return (
    <Fragment>
      <div className={classes}>
        {hasHeroImage && (
          <div className="profile-page-hero-image-wrapper">
            <img
              src={accountProfile.hero_image.url}
              className="profile-page-hero-image"
              alt="Hero"
            />
          </div>
        )}
        {displayName && (
          <Fragment>
            {onGoBack && (
              <Button
                btnSize="sm"
                btnStyle="link"
                className="profile-hero-back-button-link mt-2"
                onClick={onGoBack}
              >
                <Angle direction="left" size />
                <span className="ml-1">Back</span>
              </Button>
            )}
            <div className="profile-page-user-info">
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center flex-row mr-auto">
                  {!isProAccount(accountProfile) && (
                    <img
                      className="profile-page-hero-account-avatar mr-2"
                      src={accountAvatarUrl}
                      alt="Profile Avatar"
                    />
                  )}

                  {accountProfile.logo_image?.url &&
                    isProAccount(accountProfile) && (
                      <Fragment>
                        <img
                          alt="logo"
                          className="user-logo mr-2"
                          src={accountProfile.logo_image.url}
                        />
                      </Fragment>
                    )}
                  <div>
                    <p className="h4 mb-0">{displayName}</p>
                    {username && (
                      <p className="text-xs text-light text-bold mb-0">
                        {username} &bull;{' '}
                        {`${eventCount} ${pluralize('event', eventCount)}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="dropdown-menu-dark ml-auto align-self-center">
                <DropdownMenu
                  closeOnClickWithin
                  placement="bottom-end"
                  toggle={<DotTrigger dotType="vertical" />}
                >
                  <Button
                    btnStyle="reset"
                    className="dropdown-item text-bold"
                    onClick={() => {
                      openModal(ACCOUNT_SHARE_MODAL, {
                        account: accountProfile,
                        shareUrl: `https://waldophotos.com/@${accountProfile.username}`,
                      });
                    }}
                  >
                    Share
                  </Button>
                  {emailAddress && (
                    <Button
                      btnStyle="reset"
                      className="dropdown-item text-bold"
                      href={`mailto:${emailAddress}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Contact Us
                    </Button>
                  )}
                </DropdownMenu>
              </div>
            </div>
          </Fragment>
        )}
      </div>

      <AccountBlockModal />
      <AccountShareModal />
    </Fragment>
  );
};

ProfilePageHero.propTypes = propTypes;

export default ProfilePageHero;
